import { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { API } from '../config/api';

/**
 * Available reactions - loaded from backend API
 * Fallback list used until API responds
 */
export let REACTIONS = ["👍🏼", "❤️", "😭", "😲", "😂", "🫣", "😘", "💤", "💯", "🔥", "🎵"];

let _reactionsLoaded = false;
let _reactionLabels = {};

/**
 * Reaction labels for accessibility - loaded from API
 */
const REACTION_LABELS = {
  "👍🏼": "Like", "❤️": "Love", "😭": "Crying", "😲": "Shocked",
  "😂": "Laughing", "🫣": "Shy", "😘": "Kiss", "💤": "Sleepy",
  "💯": "Hundred", "🔥": "Fire", "🎵": "Music"
};

function useReactionTypes() {
  const [types, setTypes] = useState(REACTIONS);
  
  useEffect(() => {
    if (_reactionsLoaded) { setTypes(REACTIONS); return; }
    fetch(`${API}/reaction-types`).then(r => r.json()).then(d => {
      if (d.reactions?.length) {
        REACTIONS = d.reactions.map(r => r.emoji);
        d.reactions.forEach(r => { _reactionLabels[r.emoji] = r.name; REACTION_LABELS[r.emoji] = r.name; });
        _reactionsLoaded = true;
        setTypes(REACTIONS);
      }
    }).catch(() => {});
  }, []);
  
  return types;
}

/**
 * ReactionPicker - Emoji reaction picker for posts
 * 
 * Props:
 * - onSelect: (reaction: string) => void
 * - selectedReaction: string | null - Currently selected reaction
 * - topReactions: string[] - Top reactions to show by default
 * - className: string
 * - disabled: boolean
 */
export function ReactionPicker({ 
  onSelect, 
  selectedReaction, 
  topReactions = [], 
  className,
  disabled = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [holdTimer, setHoldTimer] = useState(null);
  const pickerRef = useRef(null);
  const reactionTypes = useReactionTypes();

  // Close picker when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle quick tap (toggle default reaction)
  const handleQuickTap = () => {
    if (disabled) return;
    
    if (selectedReaction) {
      // Remove reaction
      onSelect(null);
    } else {
      // Add default reaction (👍🏼)
      onSelect('👍🏼');
    }
  };

  // Handle long press to open picker
  const handleMouseDown = () => {
    if (disabled) return;
    
    const timer = setTimeout(() => {
      setIsOpen(true);
      setHoldTimer(null);
    }, 400); // 400ms hold to open picker
    setHoldTimer(timer);
  };

  const handleMouseUp = () => {
    if (holdTimer) {
      clearTimeout(holdTimer);
      setHoldTimer(null);
    }
  };

  const handleMouseLeave = () => {
    if (holdTimer) {
      clearTimeout(holdTimer);
      setHoldTimer(null);
    }
  };

  // Handle reaction selection
  const handleSelectReaction = (reaction) => {
    if (disabled) return;
    onSelect(reaction);
    setIsOpen(false);
  };

  // Determine which reactions to show in collapsed state
  const quickReactions = topReactions.length > 0 
    ? [...new Set([...topReactions, '❤️'])].slice(0, 3)
    : ['❤️'];

  return (
    <div ref={pickerRef} className={cn("relative inline-block", className)}>
      {/* Main Button */}
      <Button
        variant="ghost"
        size="sm"
        disabled={disabled}
        onClick={handleQuickTap}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleMouseDown}
        onTouchEnd={handleMouseUp}
        className={cn(
          "gap-2 transition-all duration-200 hover:scale-105",
          selectedReaction ? "text-red-500 hover:text-red-400" : "hover:text-red-500"
        )}
        data-testid="reaction-button"
      >
        {selectedReaction ? (
          <span className="text-lg">{selectedReaction}</span>
        ) : (
          <span className="text-lg">❤️</span>
        )}
        <span className="hidden sm:inline">
          {selectedReaction ? 'Reacted' : 'React'}
        </span>
      </Button>

      {/* Reaction Picker Popup */}
      {isOpen && (
        <div 
          className="absolute bottom-full left-0 mb-2 p-2 bg-background/95 backdrop-blur-lg border border-border rounded-full shadow-xl z-50 animate-in fade-in slide-in-from-bottom-2 duration-200"
          data-testid="reaction-picker"
        >
          <div className="flex gap-1">
            {reactionTypes.map((reaction) => (
              <button
                key={reaction}
                onClick={() => handleSelectReaction(reaction)}
                className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center text-xl transition-all duration-150 hover:scale-125 hover:bg-muted/50",
                  selectedReaction === reaction && "bg-muted/60 scale-110"
                )}
                title={REACTION_LABELS[reaction]}
                data-testid={`reaction-${reaction}`}
              >
                {reaction}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * ReactionSummary - Shows reaction counts with top emojis (Facebook-style)
 * 
 * Props:
 * - reactions: { reaction_counts: Record<string, number>, total_count: number, top_reactions: string[] }
 * - onClick: () => void - Open reaction details modal
 * - compact: boolean - Use compact display
 */
export function ReactionSummary({ reactions, onClick, compact = false }) {
  if (!reactions || reactions.total_count === 0) {
    return null;
  }

  const { reaction_counts, total_count, top_reactions } = reactions;

  // Get unique top reactions (Facebook shows up to 3 types)
  const uniqueReactions = [...new Set(top_reactions || [])].slice(0, 3);
  
  // If no top reactions, show heart by default
  if (uniqueReactions.length === 0 && total_count > 0) {
    uniqueReactions.push('❤️');
  }

  return (
    <button 
      onClick={onClick}
      className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors group"
      data-testid="reaction-summary"
    >
      {/* Stacked reaction emojis with colored backgrounds (Facebook-style) */}
      <span className="flex -space-x-1">
        {uniqueReactions.map((emoji, i) => (
          <span 
            key={emoji} 
            className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gradient-to-br from-red-500 to-pink-500 text-xs shadow-sm ring-2 ring-background"
            style={{ zIndex: 10 - i }}
          >
            {emoji}
          </span>
        ))}
      </span>
      
      {/* Count - Facebook shows just number or "You and X others" */}
      <span className="group-hover:underline">
        {compact ? total_count : (
          total_count === 1 ? '1' : total_count.toLocaleString()
        )}
      </span>
    </button>
  );
}

export default ReactionPicker;
